// Builds a customer statement of account from their invoices: each invoice
// as a charge, each dated installment in its payment history as a credit,
// in date order with a running balance. Shared by the on-screen Customer
// Ledger (Reports page) and its PDF so the two can never disagree.

const invoiceDate = (inv) => (inv.issue_date || inv.created_at || "").toString().slice(0, 10);

export const invoiceTotal = (inv) => (Number(inv.amount) || 0) + (Number(inv.tax) || 0);

export function buildStatement(invoices) {
  const entries = [];
  invoices.forEach((inv) => {
    const date = invoiceDate(inv);
    const title = inv.invoice_title || inv.doc_type || "Invoice";
    entries.push({
      key: `${inv.id}-inv`, kind: "invoice", date, sortDate: date, order: 0,
      invoice_no: inv.invoice_no, description: title, debit: invoiceTotal(inv), credit: 0,
    });
    (inv.payments || []).forEach((p, i) => {
      const how = [p.method, p.reference].filter(Boolean).join(" · ");
      // The invoice's title makes a payment traceable at a glance, next to
      // the invoice number, without opening the invoice.
      const forWhat = inv.invoice_title ? ` (${inv.invoice_title})` : "";
      entries.push({
        key: `${inv.id}-pay-${p.id || i}`, kind: "payment",
        date: p.date || null,
        // An installment whose date was never recorded sits right after its
        // own invoice -- the only placement that's never misleading.
        sortDate: p.date || date, order: p.date ? 1 : 2,
        invoice_no: inv.invoice_no,
        description: `Payment received${forWhat}${how ? ` — ${how}` : ""}`,
        undated: !p.date,
        debit: 0, credit: Number(p.amount) || 0,
      });
    });
  });

  entries.sort((a, b) =>
    a.sortDate.localeCompare(b.sortDate)
    || a.invoice_no.localeCompare(b.invoice_no)
    || a.order - b.order);

  let balance = 0;
  entries.forEach((e) => { balance += e.debit - e.credit; e.balance = balance; });

  const invoiced = entries.reduce((s, e) => s + e.debit, 0);
  const paid = entries.reduce((s, e) => s + e.credit, 0);
  const undatedCount = entries.filter((e) => e.undated).length;
  return { entries, totals: { invoiced, paid, outstanding: invoiced - paid }, undatedCount };
}
